import type {AvatarProps as MaterialAvatarProps} from '@mui/material/Avatar';
import MaterialAvatar from '@mui/material/Avatar';
import {styled} from '@mui/material/styles';
import SvgIcon, {SvgIconProps} from '@mui/material/SvgIcon';
import {Children, cloneElement, ComponentType, forwardRef, isValidElement, useMemo} from 'react';
import {FuelAvatarColorKey, getAvatarColor} from '../helpers/getColor';

type FuelAvatarSize = 'small' | 'medium' | 'large';

export interface AvatarProps extends MaterialAvatarProps {
    /**
     * Avatar Color
     */
    color?: FuelAvatarColorKey;
    /**
     * Avatar Size
     */
    size?: FuelAvatarSize;
}

const StyledAvatar = styled(MaterialAvatar)<AvatarProps>(({
    size, color
}) => ({
    height: '2.5rem',
    width: '2.5rem',
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: '1.75rem',
    ...size === 'small' && {
        height: '2rem',
        width: '2rem',
        fontSize: '0.875rem',
        lineHeight: '1.25rem'
    },
    ...size === 'medium' && {
        height: '2.5rem',
        width: '2.5rem',
        fontSize: '1.125rem',
        lineHeight: '1.75rem'
    },
    ...size === 'large' && {
        height: '3.5rem',
        width: '3.5rem',
        fontSize: '1.5rem',
        lineHeight: '2rem'
    },

    backgroundColor: getAvatarColor('default'),
    ...color && {
        backgroundColor: getAvatarColor(color)
    }
}));

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
    ({
        children, color, size = 'medium', ...props
    }, ref): React.ReactElement => {
        const iconSize = useMemo(() => {
            if (size === 'small') {
                return {
                    height: '1rem',
                    width: '1rem'
                };
            }

            if (size === 'large') {
                return {
                    height: '2.25rem',
                    width: '2.25rem'

                };
            }

            return {
                height: '1.5rem',
                width: '1.5rem'
            };
        }, [size]);

        return (
            <StyledAvatar
                color={color}
                ref={ref}
                size={size}
                {...props}
                variant='circular'
            >
                {Children.map(children, (child) => {
                    if (isValidElement(child) && (child.type as ComponentType<SvgIconProps>).name === SvgIcon.name) {
                        return cloneElement(child, {
                            ...child.props,
                            style: iconSize
                        });
                    }

                    return child;
                })}
            </StyledAvatar>
        );
    }
);

Avatar.displayName = 'Avatar';
